import {Box, Chip, Stack} from '@mui/material';
import {useMemo} from 'react';
import {ExamModule, StandardSubject, SubjectLevel} from '@cp/model';
import {env} from '../../config/env';
import SearchField from '../../components/styled/SearchField';
import ToggleFilter from '../../components/styled/ToggleFilter';

export interface CoursesAvailableFiltersProps {
  modules: ExamModule[];
  selectedSubjects: StandardSubject[];
  selectedLevels: SubjectLevel[];
  selectedExamBoards: string[];
  onSearchChange: (query: string) => void;
  onSubjectToggle: (subject: StandardSubject) => void;
  onLevelToggle: (level: SubjectLevel) => void;
  onExamBoardToggle: (examBoard: string) => void;
  onClearFilters: () => void;
  searchFieldKey?: number;
}

export default function CoursesAvailableFilters({
  modules,
  selectedSubjects,
  selectedLevels,
  selectedExamBoards,
  onSearchChange,
  onSubjectToggle,
  onLevelToggle,
  onExamBoardToggle,
  onClearFilters,
  searchFieldKey,
}: CoursesAvailableFiltersProps) {
  // Extract unique values from modules
  const availableSubjects = useMemo(() => {
    const subjects = new Set<StandardSubject>();
    modules.forEach((module) => {
      if (module.subject) {
        subjects.add(module.subject);
      }
    });
    return Array.from(subjects);
  }, [modules]);

  const availableLevels = useMemo(() => {
    const levels = new Set<SubjectLevel>();
    modules.forEach((module) => {
      if (module.level) {
        levels.add(module.level);
      }
    });
    return Array.from(levels);
  }, [modules]);

  const availableExamBoards = useMemo(() => {
    const examBoards = new Set<string>();
    modules.forEach((module) => {
      if (module.examBoard) {
        examBoards.add(module.examBoard);
      }
    });
    return Array.from(examBoards).sort();
  }, [modules]);

  const hasActiveFilters = selectedSubjects.length > 0 || selectedLevels.length > 0 || selectedExamBoards.length > 0;

  return (
    <Stack spacing={2}>
      {/* Search Bar */}
      <SearchField key={searchFieldKey} onChange={onSearchChange} data-testid="courses-available-search-field" />

      {/* Filter Chips */}
      {/* Subject Filter */}
      <ToggleFilter
        label="Subject"
        options={availableSubjects}
        selectedValues={selectedSubjects}
        onToggle={onSubjectToggle}
        getLabel={(subject) => env.subjects[subject]?.label || subject}
        minOptionsToShow={2}
      />

      {/* Level Filter */}
      <ToggleFilter
        label="Level"
        options={availableLevels}
        selectedValues={selectedLevels}
        onToggle={onLevelToggle}
        getLabel={(level) => env.levels[level]?.label || level}
        minOptionsToShow={2}
      />

      {/* Exam Board Filter */}
      <ToggleFilter
        label="Exam Board"
        options={availableExamBoards}
        selectedValues={selectedExamBoards}
        onToggle={onExamBoardToggle}
        minOptionsToShow={1}
      />

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Box>
          <Chip
            label="Clear all filters"
            onClick={onClearFilters}
            color="default"
            variant="outlined"
            sx={{
              cursor: 'pointer',
              borderColor: 'error.main',
              color: 'error.main',
              '&:hover': {
                bgcolor: 'error.main',
                color: 'white',
              },
            }}
          />
        </Box>
      )}
    </Stack>
  );
}
